// functions/retry.ts
export type RetryOptions = {
    maxAttempts: number;          // total tries incl. first, e.g. 5
    baseDelayMs?: number;         // starting delay, default 400
    maxDelayMs?: number;          // cap, default 12000
    jitter?: boolean;             // add 0.5x–1.5x jitter, default true
    retryOn?: (err: unknown, attempt: number) => boolean;
};

const defaultRetryableStatus = new Set([408, 425, 429, 500, 502, 503, 504]);

export const defaultRetryOn = (err: any) => {
    // Axios-like error shape
    if (!err || typeof err !== "object") return false;
    if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return false; // explicit cancel
    const status = err?.response?.status as number | undefined;
    if (status && defaultRetryableStatus.has(status)) return true;
    // network/DNS/timeouts
    return !!(err?.code && /ECONN|ETIMEDOUT|ENET|EAI_AGAIN/i.test(String(err.code)));
};

const delay = (ms: number, signal?: AbortSignal) =>
    new Promise<void>((resolve, reject) => {
        const id = setTimeout(resolve, ms);
        if (signal) {
            const onAbort = () => { clearTimeout(id); reject(new DOMException("Aborted", "AbortError")); };
            if (signal.aborted) onAbort();
            else signal.addEventListener("abort", onAbort, { once: true });
        }
    });

const computeDelay = (attempt: number, base = 400, cap = 12000, jitter = true) => {
    const exp = Math.min(cap, base * Math.pow(2, Math.max(0, attempt - 1)));
    if (!jitter) return exp;
    const factor = 0.5 + Math.random(); // 0.5–1.5
    return Math.round(exp * factor);
};

/** Run fn with retries; respects AbortSignal between attempts. */
export async function withRetry<T>(
    fn: (attempt: number) => Promise<T>,
    opts: RetryOptions,
    signal?: AbortSignal,
    onAttempt?: (attempt: number, err?: unknown) => void
): Promise<T> {
    const {
        maxAttempts,
        baseDelayMs = 400,
        maxDelayMs = 12000,
        jitter = true,
        retryOn = defaultRetryOn,
    } = opts;

    let attempt = 1;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        try {
            onAttempt?.(attempt);
            return await fn(attempt);
        } catch (err) {
            onAttempt?.(attempt, err);
            if (attempt >= maxAttempts || !retryOn(err, attempt)) throw err;
            const wait = computeDelay(attempt, baseDelayMs, maxDelayMs, jitter);
            await delay(wait, signal);
            attempt += 1;
        }
    }
}
